
import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import { resolveAllPlatforms, hasFloorAhead } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type SparkImpState = 'PATROL' | 'AIM' | 'FIRING' | 'COOLDOWN' | 'RETREAT' | 'HURT' | 'DEAD';

/** Width/height of the SparkImp hitbox in logical pixels. */
export const SPARK_IMP_WIDTH = 22;
export const SPARK_IMP_HEIGHT = 24;

/** Patrol walk speed (px/s). */
const PATROL_SPEED = 50;
/** Retreat scuttle speed — faster than patrol; the imp panics (px/s). */
const RETREAT_SPEED = 90;
/** Horizontal range at which Nova triggers AIM (px). */
const FIRE_RANGE = 220;
/** Horizontal range at which Nova triggers RETREAT — overrides AIM (px). */
const RETREAT_RANGE = 90;
/** Hysteresis past RETREAT_RANGE before returning to PATROL (px). Prevents jitter at the band edge. */
const RETREAT_HYSTERESIS = 16;
/** Max vertical |Δy| for Nova to count as in-band — keeps imps on lower ledges from twitching at Nova above. */
const VERTICAL_BAND = 80;

/** Telegraph window before firing (s). */
const AIM_DURATION = 0.35;
/** Firing window (s). */
const FIRING_DURATION = 0.12;
/** Recovery before re-engaging (s). */
const COOLDOWN_DURATION = 1.2;
/** Hurt-flash duration before DEAD (s). */
const HURT_DURATION = 0.25;

/** Render colour for greybox. */
export const SPARK_IMP_COLOR = '#e8d24a'; // electric-yellow

// DECISION: Reusable boxes to avoid per-tick allocations (mirrors Nibbler / Shellback).
const _hitbox = { x: 0, y: 0, width: SPARK_IMP_WIDTH, height: SPARK_IMP_HEIGHT };

/**
 * Result of a projectile or stomp impact. Mirrors the union used by Shellback
 * / SentinelBloom so combat code branches on a single type across enemies.
 *
 * DECISION: No `'KILLED'` — hits start HURT → DEAD which completes inside
 * `update()`; returning `'HURT'` keeps the value consistent with `state`.
 * `'BLOCKED'` is never returned today (no shield) but stays in the union for
 * cross-enemy type alignment.
 */
export type SparkImpHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/**
 * SparkImp — skittish ranged enemy.
 *
 * Spawn with `new SparkImp(x, y)`. Call `update(dt, level, nova)` each fixed
 * tick. Read `fireRequested` after `update()` to spawn the spark exactly
 * once per cycle. Read `dead` to know when to remove from the scene.
 */
export class SparkImp extends BaseEntity {
  public readonly entityType = 'SparkImp';
  public readonly color = SPARK_IMP_COLOR;
  public state: SparkImpState = 'PATROL';

  /**
   * True once a hit has started the death sequence (`HURT`) or it has
   * finished (`DEAD`). See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'HURT' || this.state === 'DEAD';
  }
  /** Controls which way the sprite faces. Starts walking right. */
  public facingRight = true;
  /** Single-tick fire pulse — set on AIM → FIRING, cleared at the top of the next `update()`. */
  public fireRequested = false;
  private _phaseTimer = 0;

  constructor(x: number, y: number) {
    super(x, y, SPARK_IMP_WIDTH, SPARK_IMP_HEIGHT);
    this.vx = PATROL_SPEED;
  }

  /**
   * Advance the imp by one fixed tick.
   * @param dt    Fixed-step delta in seconds.
   * @param level Current level geometry — read-only.
   * @param nova  Optional Nova snapshot. Undefined → imp patrols, no AI reactions.
   */
  public update(dt: number, level: LevelRuntime, nova?: NovaRuntime): void {
    if (this.state === 'DEAD') return;

    // DECISION: Clear the prior-tick pulse at the TOP (mirrors SentinelBloom)
    // so combat systems that run between enemy updates see the pulse for a
    // full tick before it expires.
    this.fireRequested = false;

    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);

    if (this.state === 'HURT') {
      this._phaseTimer -= dt;
      this._integrateAndResolve(dt, level);
      if (this._phaseTimer <= 0) {
        this.state = 'DEAD';
        this.dead = true;
        Logger.debug('SparkImp', 'Death after hurt');
      }
      return;
    }

    this._tickAI(dt, nova);
    this._integrateAndResolve(dt, level);
    if (this.state === 'PATROL' || this.state === 'RETREAT') {
      this._handleGroundEdges(level);
    }
  }

  /** Choose vx + drive the state machine. Pure decision logic — no position writes. */
  private _tickAI(dt: number, nova?: NovaRuntime): void {
    const inFireBand = nova !== undefined && this._isNovaInBand(nova, FIRE_RANGE);
    const inRetreatBand = nova !== undefined && this._isNovaInBand(nova, RETREAT_RANGE);

    switch (this.state) {
      case 'PATROL':
        if (inRetreatBand && nova) this._enterRetreat(nova);
        else if (inFireBand && nova) this._enterAim(nova);
        break;
      case 'AIM':
        if (inRetreatBand && nova) {
          this._enterRetreat(nova);
        } else if (!inFireBand) {
          this._enterPatrol();
        } else {
          this._phaseTimer -= dt;
          if (this._phaseTimer <= 0) this._enterFiring();
        }
        break;
      case 'FIRING':
        this._phaseTimer -= dt;
        if (this._phaseTimer <= 0) {
          this.state = 'COOLDOWN';
          this._phaseTimer = COOLDOWN_DURATION;
          Logger.debug('SparkImp', 'Cooldown');
        }
        break;
      case 'COOLDOWN':
        if (inRetreatBand && nova) {
          this._enterRetreat(nova);
        } else {
          this._phaseTimer -= dt;
          if (this._phaseTimer <= 0) this._enterPatrol();
        }
        break;
      case 'RETREAT':
        if (!nova || !this._isNovaInBand(nova, RETREAT_RANGE + RETREAT_HYSTERESIS)) {
          this._enterPatrol();
        } else {
          // Re-pin velocity each tick — Nova might have crossed sides.
          this.vx = nova.x < this.x ? RETREAT_SPEED : -RETREAT_SPEED;
          this.facingRight = this.vx > 0;
        }
        break;
    }
  }

  /** True iff Nova is within `range` horizontally AND within VERTICAL_BAND vertically. */
  private _isNovaInBand(nova: NovaRuntime, range: number): boolean {
    return Math.abs(nova.x - this.x) <= range && Math.abs(nova.y - this.y) <= VERTICAL_BAND;
  }

  private _enterPatrol(): void {
    this.vx = this.facingRight ? PATROL_SPEED : -PATROL_SPEED;
    this.state = 'PATROL';
    Logger.debug('SparkImp', 'Patrol');
  }

  private _enterAim(nova: NovaRuntime): void {
    this.facingRight = nova.x >= this.x;
    this.vx = 0;
    this.state = 'AIM';
    this._phaseTimer = AIM_DURATION;
    Logger.debug('SparkImp', 'Aim');
  }

  private _enterFiring(): void {
    this.state = 'FIRING';
    this.fireRequested = true;
    this._phaseTimer = FIRING_DURATION;
    Logger.debug('SparkImp', 'Firing');
  }

  private _enterRetreat(nova: NovaRuntime): void {
    this.vx = nova.x < this.x ? RETREAT_SPEED : -RETREAT_SPEED;
    this.facingRight = this.vx > 0;
    this.state = 'RETREAT';
    Logger.debug('SparkImp', 'Retreat');
  }

  private _integrateAndResolve(dt: number, level: LevelRuntime): void {
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const r = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    this.x = r.x;
    this.y = r.y;
    if (r.landedOnTop) this.vy = 0;
    if ((r.hitLeft || r.hitRight) && (this.state === 'PATROL' || this.state === 'RETREAT')) {
      this.vx = -this.vx;
      this.facingRight = this.vx > 0;
      Logger.debug('SparkImp', 'Turn at wall');
    }
  }

  private _handleGroundEdges(level: LevelRuntime): void {
    if (this.vy !== 0) return; // Only act when grounded (resolver zeroed vy).
    const hasFloor = hasFloorAhead(
      this.x,
      this.y,
      this.width,
      this.height,
      this.vx,
      level.platforms,
    );
    if (!hasFloor && this.vx !== 0) {
      this.vx = -this.vx;
      this.facingRight = this.vx > 0;
      Logger.debug('SparkImp', 'Turn at edge');
    }
  }

  /**
   * Apply one projectile hit. No shield — any shard connects.
   * @returns `'HURT'` on a hit that started the death sequence, `'IGNORED'` if
   *          already dead/hurt. Never returns `'BLOCKED'` today.
   */
  public applyShardHit(): SparkImpHitResult {
    if (this.state === 'DEAD' || this.state === 'HURT') return 'IGNORED';
    this._enterHurt();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. No protection from above.
   * @returns `true` if the stomp started the death sequence; `false` if already dead/hurt.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD' || this.state === 'HURT') return false;
    this._enterHurt();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts) — delegates to
   * the specialised methods. Kept thin so `combatStep` can drive SparkImp
   * without an external adapter once it is wired into `gameState.enemies`.
   * @param fromStomp `true` for stomp contact, `false`/omitted for shard hits.
   * @returns `true` if this hit started the death sequence; `false` otherwise.
   */
  public applyHit(fromStomp = false): boolean {
    return fromStomp ? this.applyStomp() : this.applyShardHit() === 'HURT';
  }

  private _enterHurt(): void {
    this.state = 'HURT';
    this._phaseTimer = HURT_DURATION;
    this.vx = 0;
    this.fireRequested = false;
    Logger.debug('SparkImp', 'Hit — entering HURT');
  }
}
